import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import UserPage from 'flarum/forum/components/UserPage';
import IndexPage from 'flarum/forum/components/IndexPage';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import LinkButton from 'flarum/common/components/LinkButton';
import Button from 'flarum/common/components/Button';
import ChallengeModal from './components/ChallengeModal';
import BattlePassModal from './components/BattlePassModal';
import QuestsModal from './components/QuestsModal';
import { getContrastColor } from '../common';
import { battlePassConfig, battlePassLevelFor } from './battlePass';
import { activeQuests, questsEnabled } from './quests';
function isArenaTagPage() {
  const arenaTagId = String(app.forum.attribute('arenaBattleTagId') ?? '');
  if (!arenaTagId || arenaTagId === '0') return false;
  const currentSlug = m.route.param('tags') ?? m.route.param('tag') ?? '';
  if (!currentSlug) return false;
  const allTags = app.store.all('tags');
  const tagBySlug = allTags.find(t => {
    const s = t.slug?.() ?? t.attribute?.('slug') ?? '';
    return s.toLowerCase() === currentSlug.toLowerCase();
  });
  if (tagBySlug) return String(tagBySlug.id?.() ?? tagBySlug.id) === arenaTagId;
  const tagById = app.store.getById('tags', arenaTagId);
  if (tagById) {
    const slug = tagById.slug?.() ?? tagById.attribute?.('slug') ?? '';
    return slug.toLowerCase() === currentSlug.toLowerCase();
  }
  return false;
}
function getArenaTag() {
  const arenaTagId = String(app.forum.attribute('arenaBattleTagId') ?? '');
  if (!arenaTagId || arenaTagId === '0') return null;
  return app.store.getById('tags', arenaTagId) ?? null;
}
function arenaButtonProps() {
  const tag = getArenaTag();
  const color = tag?.color?.() ?? tag?.attribute?.('color') ?? null;
  if (color) {
    const textColor = getContrastColor(color);
    return {
      className: 'Button Button--primary Button--block IndexPage-newDiscussion',
      style: {
        background: color,
        'border-color': color,
        color: textColor
      }
    };
  }
  return {
    className: 'Button Button--primary Button--block IndexPage-newDiscussion'
  };
}

/**
 * "Challenge" button + "Battle Pass" button (if levels are configured) +
 * "Quests" button (if at least one active quest is configured) + "How to
 * play" button (if a link is set in admin).
 *
 * Battle Pass and Quests are visible to guests too — they double as a
 * "what you can earn" showcase.
 *
 * Visual order is Challenge, Battle Pass, Quests, How to play, controlled
 * by CSS `order` (see .ArenaTagButtons-* in forum.less), not DOM position.
 * When an odd number of buttons ends up rendered, the last one by that
 * order gets .ArenaTagButtons-lastAlone so it spans the row alone instead
 * of sitting at half width.
 */
function arenaButtons() {
  const props = arenaButtonProps();
  const howToPlayUrl = app.forum.attribute('arenaHowToPlayUrl') || '';
  const howToPlayNewTab = app.forum.attribute('arenaHowToPlayNewTab') !== false;
  const battlePass = battlePassConfig();
  const showBattlePass = battlePass.configured;
  const showQuests = questsEnabled() && activeQuests().length > 0;

  // Per-button icon/text overrides from the admin panel's "Кнопки" section —
  // empty text falls back to the built-in translated default, which for
  // Battle Pass is itself dynamic (level N for logged-in users, plain title
  // for guests).
  const challengeIcon = app.forum.attribute('arenaButtonsChallengeIcon') || 'fas fa-swords';
  const challengeText = app.forum.attribute('arenaButtonsChallengeText') || '';
  const howToPlayIcon = app.forum.attribute('arenaButtonsHowToPlayIcon') || 'fas fa-question-circle';
  const howToPlayText = app.forum.attribute('arenaButtonsHowToPlayText') || '';
  const battlePassIcon = app.forum.attribute('arenaButtonsBattlePassIcon') || 'fas fa-medal';
  const battlePassText = app.forum.attribute('arenaButtonsBattlePassText') || '';
  const questsIcon = app.forum.attribute('arenaButtonsQuestsIcon') || 'fas fa-tasks';
  const questsText = app.forum.attribute('arenaButtonsQuestsText') || '';

  // Same fixed sequence as the CSS `order` values — whichever key ends up
  // LAST here (among the buttons actually shown) gets forced full-width
  // when the total count is odd.
  const shown = ['challenge', showBattlePass && 'battlePass', showQuests && 'quests', howToPlayUrl && 'howToPlay'].filter(k => Boolean(k));
  const lastAlone = shown.length % 2 === 1 ? shown[shown.length - 1] : null;
  const aloneClass = key => key === lastAlone ? ' ArenaTagButtons-lastAlone' : '';
  return m("div", {
    className: "ArenaTagButtons"
  }, m(Button, {
    className: props.className + ' ArenaTagButtons-challenge' + aloneClass('challenge'),
    style: props.style,
    icon: challengeIcon,
    onclick: () => app.session.user ? app.modal.show(ChallengeModal) : app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'))
  }, challengeText || app.translator.trans('forumaker-arena.forum.challenge.btn')), showBattlePass && m(Button, {
    className: `Button Button--block ArenaTagButtons-battlePass${aloneClass('battlePass')}`,
    icon: battlePassIcon,
    onclick: () => app.modal.show(BattlePassModal)
  }, battlePassText || (app.session.user ? app.translator.trans('forumaker-arena.forum.battle_pass.btn_level', {
    level: String(battlePassLevelFor(app.session.user, battlePass))
  }) : app.translator.trans('forumaker-arena.forum.battle_pass.title'))), showQuests && m(Button, {
    className: `Button Button--block ArenaTagButtons-quests${aloneClass('quests')}`,
    icon: questsIcon,
    onclick: () => app.modal.show(QuestsModal)
  }, questsText || app.translator.trans('forumaker-arena.forum.quests.btn')), howToPlayUrl && m("a", {
    className: `Button Button--block ArenaTagButtons-howToPlay${aloneClass('howToPlay')}`,
    href: howToPlayUrl,
    target: howToPlayNewTab ? '_blank' : undefined,
    rel: howToPlayNewTab ? 'noopener noreferrer' : undefined
  }, m("i", {
    className: `Button-icon ${howToPlayIcon}`
  }), m("span", {
    className: "Button-label"
  }, howToPlayText || app.translator.trans('forumaker-arena.forum.how_to_play.btn'))));
}
export default function addArenaTab() {
  // "Arena" tab on the profile page
  extend(UserPage.prototype, 'navItems', function (items) {
    const user = this.user;
    if (!user) return;
    const stats = user.attribute('arenaStats');
    if (!stats && app.session.user?.id() !== user.id()) return;
    items.add('arena', m(LinkButton, {
      href: app.route('user.arena', {
        username: user.username()
      }),
      icon: "fas fa-dungeon"
    }, app.translator.trans('forumaker-arena.forum.user.arena_tab')), 70);
  });

  // Buttons on the Arena tag page. Flarum 2.x: the sidebar is a separate
  // IndexSidebar component with an items() method. Desktop: buttons at the
  // top of the sidebar instead of "Create discussion". Mobile: the same
  // item renders inline between the hero and the discussion list.
  extend(IndexSidebar.prototype, 'items', function (items) {
    if (!isArenaTagPage()) return;

    // Пункт «Создать тему» не удаляем, а прячем классом: flarum/tags в своём
    // extend читает items.get('newDiscussion') и падает, если пункта уже нет.
    // Порядок расширений не гарантирован, поэтому оставляем узел на месте.
    const newDiscussion = items.has('newDiscussion') ? items.get('newDiscussion') : null;
    if (newDiscussion?.attrs) {
      newDiscussion.attrs.itemClassName = [newDiscussion.attrs.itemClassName, 'ArenaSidebarItem--hidden'].filter(Boolean).join(' ');
    }
    items.add('challengeBattle', arenaButtons(), 100);
  });

  // "Мои бои" toggle next to the sort dropdown on the Arena tag page —
  // filters the discussion list down to battles the current user is
  // fighting in (either side). Backed by filter[arenaMyBattles] (see
  // Search\MyBattlesFilter in extend.php).
  extend(IndexPage.prototype, 'viewItems', function (items) {
    if (!isArenaTagPage() || !app.session.user) return;
    const discussions = app.discussions;
    const active = !!discussions.params?.filter?.arenaMyBattles;
    items.add('arenaMyBattles', m(Button, {
      className: "Button",
      icon: active ? 'fas fa-check' : undefined,
      onclick: () => {
        // Not DiscussionListState::changeFilter() — it sets the key's
        // value to `undefined` rather than deleting it, and the key
        // survives that (both the object spread and the request
        // serializer keep an `undefined`-valued key around), so the API
        // still receives filter[arenaMyBattles] on "off" and keeps
        // filtering. Deleting the key outright is the only way "off"
        // actually clears it.
        const filter = {
          ...discussions.params?.filter
        };
        if (active) {
          delete filter.arenaMyBattles;
        } else {
          filter.arenaMyBattles = '1';
        }
        discussions.refreshParams({
          ...discussions.params,
          filter
        }, 1);
      }
    }, app.translator.trans('forumaker-arena.forum.my_battles.btn')),
    // Just under the sort dropdown's default priority (0) so it renders
    // right after it, still to the left of the refresh/mark-read icons
    // (those live in actionItems(), a separate <ul>).
    -10);
  });
}